import API from "./api";

// Login user and return the token and user data
export const loginUser = async (username, password) => {
    try {
        const response = await API.post("/users/login", { username, password });

        // Log the entire response data for debugging
        console.log("Login API response:", response.data);

        if (response.data.token) {
            // Store token in localStorage
            localStorage.setItem("token", response.data.token);

            // Store user data if available
            if (response.data.user) {
                console.log("Storing user data:", response.data.user);
                localStorage.setItem("user", JSON.stringify(response.data.user));
            } else {
                console.warn("No user data found in login response");
            }
        } else {
            console.warn("No token found in login response");
        }

        return response.data;
    } catch (error) {
        console.error("Login error:", error.response?.data || error.message);
        throw error;
    }
};

// Register a new user
export const registerUser = async (userData) => {
    try {
        const response = await API.post("/users/register", userData);
        return response.data;
    } catch (error) {
        console.error("Error registering user:", error.response?.data || error.message);
        throw error;
    }
};

// Get all roles
export const getAllRoles = async () => {
    try {
        const response = await API.get("/roles");
        return response.data;
    } catch (error) {
        console.error("Error fetching roles:", error.response?.data || error.message);
        throw error;
    }
};

// Get all users
export const getAllUsers = async () => {
    try {
        const response = await API.get("/users");
        return response.data;
    } catch (error) {
        console.error("Error fetching users:", error);
        throw error;
    }
};

// Logout user
export const logoutUser = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
};

// Get current authenticated user from localStorage
export const getCurrentUser = () => {
    try {
        const userStr = localStorage.getItem("user");
        if (userStr) {
            const user = JSON.parse(userStr);
            console.log("Retrieved user from localStorage:", user);
            return user;
        }
        return null;
    } catch (error) {
        console.error("Error parsing user data:", error);
        return null;
    }
};

// Check if user is logged in
export const isAuthenticated = () => {
    return localStorage.getItem("token") !== null;
};

// Get auth token
export const getToken = () => {
    return localStorage.getItem("token");
};

// Add auth header for API calls
export const authHeader = () => {
    const token = getToken();
    if (token) {
        return { Authorization: `Bearer ${token}` };
    } else {
        return {};
    }
};

// NEW: Check if user has a specific role
export const hasRole = (roleToCheck) => {
    const user = getCurrentUser();

    // If no user or no role property, return false
    if (!user || !user.role) {
        return false;
    }

    // If roleToCheck is an array, check if user's role is in that array
    if (Array.isArray(roleToCheck)) {
        return roleToCheck.includes(user.role);
    }

    // Otherwise, just check if user's role matches the provided role
    return user.role === roleToCheck;
};

// NEW: Check if user is a Stock Keeper
export const isStockKeeper = () => {
    return hasRole("Stock Keeper");
};

// NEW: Check if user is a Cashier
export const isCashier = () => {
    return hasRole("Cashier");
};

// NEW: Check if user is an Admin or Manager (can access all features)
export const isAdmin = () => {
    return hasRole(["Manager", "Admin", "Supervisor"]);
};

export const isSupervisor = () => {
    return hasRole("Supervisor");
};

// NEW: Get appropriate dashboard path based on user role
export const getDashboardPath = () => {
    if (isStockKeeper()) {
        return "/stock-dashboard";
    } else if (isCashier()) {
        return "/cashier-dashboard";
    } else if (isSupervisor()) {
        return "/supervisor-dashboard";
    }
    return "/dashboard";
};

// Configure axios interceptors
export const setupAxiosInterceptors = () => {
    API.interceptors.request.use(
        (config) => {
            const token = localStorage.getItem("token");
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    API.interceptors.response.use(
        (response) => response,
        (error) => {
            if (error.response && error.response.status === 401) {
                console.log("Unauthorized! Logging out...");

                // Remove authentication data and reload page
                localStorage.removeItem("token");
                localStorage.removeItem("user");

                setTimeout(() => {
                    window.location.href = "/login";  // Force full reload
                }, 500);
            }
            return Promise.reject(error);
        }
    );
};